import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Send, EyeOff, ShieldAlert } from "lucide-react";
import { PageHeader, Btn, Field, inputCls, Modal, Card } from "@/components/cc";
import { useAuth } from "@/context/AuthContext";

const REGIONS = ["Africa", "North America", "Latin America and the Caribbean", "Europe", "Asia", "Middle East", "Oceania", "International or Cross-Border"];
const CATS = ["Organized Crime", "Homicide", "Financial Crime", "Cybercrime", "Fraud", "Corruption", "Human Trafficking", "Drug Trafficking", "Weapons Trafficking", "Cross-Border Crime", "Major Court Case", "International Fugitive", "Terrorism-Related Criminal Case", "Public-Sector Crime", "Other Serious Crime"];
const LEGAL = ["Person of Interest", "Questioned", "Investigated", "Detained", "Arrested", "Charged", "Accused", "Indicted", "On Trial", "Convicted", "Sentenced", "Acquitted", "Charges Dismissed", "Case Withdrawn", "Appeal Pending", "Case Closed", "Unknown"];
const STATUS_NEXT = { Monitoring: ["Developing"], Developing: ["Partially Verified"], "Partially Verified": ["Confirmed"], Confirmed: ["Charges Filed", "Court Proceedings", "Closed"], "Charges Filed": ["Court Proceedings", "Closed"], "Court Proceedings": ["Verdict Reached", "Closed"], "Verdict Reached": ["Appeal", "Closed"], Appeal: ["Closed"], Closed: ["Archived"], Archived: ["Developing"] };

const blank = () => ({ headline: "", summary: "", country: "", geographic_region: "Africa", crime_category: "Other Serious Crime", legal_status: "Unknown", confirmed_facts: "", allegations: "", disputed_facts: "", internal_notes: "", public_sources: [] });

export default function CrimeAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());
  const [sel, setSel] = useState(null);
  const [upd, setUpd] = useState({ headline: "", new_confirmed: "", new_allegation: "", legal_status_change: "", is_public: true });
  const [confirmUnpub, setConfirmUnpub] = useState(false);
  const [unpubBusy, setUnpubBusy] = useState(false);
  const { user } = useAuth();
  const isAdmin = !!user?.roles?.includes("super_admin");
  const [markModal, setMarkModal] = useState(false);
  const [unmarkModal, setUnmarkModal] = useState(false);
  const [markBusy, setMarkBusy] = useState(false);

  const load = () => api.get("/crime").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/crime", form); toast.success("Crime story created"); setOpen(false); setForm(blank()); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const openStory = async (id) => { const { data } = await api.get(`/crime/${id}`); setSel(data); };
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); await openStory(sel.story.id); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  const doUnpublish = async () => {
    if (unpubBusy) return;
    setUnpubBusy(true);
    try {
      await api.post(`/crime/${sel.story.id}/unpublish`);
      toast.success("Story removed from the public website.");
      setConfirmUnpub(false);
      await openStory(sel.story.id);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUnpubBusy(false);
    }
  };

  const setTestMarker = async (isTest) => {
    if (markBusy) return;
    setMarkBusy(true);
    try {
      await api.post(`/crime/${sel.story.id}/test-marker`, { is_test: isTest });
      toast.success(isTest ? "Marked as TEST / QA" : "TEST / QA marker removed");
      setMarkModal(false); setUnmarkModal(false);
      await openStory(sel.story.id);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setMarkBusy(false);
    }
  };

  return (
    <div className="p-6">
      <PageHeader title="Global Crime Watch" subtitle="Structured crime editorial — presumption of innocence enforced.">
        <Btn data-testid="new-crime-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> New Crime Story</Btn>
      </PageHeader>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="space-y-2" data-testid="crime-list">
          {items.length === 0 && <p className="text-sm text-zinc-600">No crime stories yet.</p>}
          {items.map((s) => (
            <Card key={s.id} className="p-3 flex items-center gap-3 hover:border-zinc-700 cursor-pointer" onClick={() => openStory(s.id)} data-testid={`crime-item-${s.id}`}>
              <div className="flex-1">
                <div className="text-sm font-medium">{s.headline}</div>
                <div className="text-xs text-zinc-500">{s.crime_category} · {s.geographic_region}</div>
              </div>
              <span className="text-[0.6rem] uppercase bg-zinc-800 px-2 py-0.5 rounded-sm">{s.story_status}</span>
              {s.is_test_record && <span data-testid={`crime-test-badge-${s.id}`} className="text-[0.6rem] font-bold uppercase bg-amber-500/20 text-amber-300 border border-amber-500/40 px-2 py-0.5 rounded-sm">TEST/QA</span>}
              <span className={`text-[0.6rem] uppercase px-2 py-0.5 rounded-sm ${s.publication_status === "published" || s.publication_status === "updated" ? "bg-emerald-600/30 text-emerald-300" : "bg-zinc-700 text-zinc-400"}`}>{s.publication_status}</span>
            </Card>
          ))}
        </div>

        {sel && (
          <Card className="p-4 h-fit sticky top-4" data-testid="crime-editor">
            <div className="flex items-center justify-between">
              <h3 className="font-tactical font-bold">{sel.story.headline}</h3>
              <div className="flex items-center gap-2">
                {sel.story.is_test_record && <span data-testid="crime-detail-test-badge" className="text-[0.6rem] font-bold uppercase bg-amber-500/20 text-amber-300 border border-amber-500/40 px-2 py-0.5 rounded-sm">TEST / QA</span>}
                <span className="text-xs text-zinc-500">{sel.story.story_status}</span>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {(STATUS_NEXT[sel.story.story_status] || []).map((t) => (
                <Btn key={t} variant="ghost" data-testid={`crime-transition-${t}`} onClick={() => act(() => api.post(`/crime/${sel.story.id}/transition`, { target: t }), `Moved to ${t}`)}>{t}</Btn>
              ))}
              {!sel.story.is_test_record && sel.story.story_status !== "Monitoring" && (
                <Btn data-testid="crime-publish-btn" onClick={() => act(() => api.post(`/crime/${sel.story.id}/publish`), "Published")}><Send className="h-4 w-4" /> Publish</Btn>
              )}
              {["Archived"].includes(sel.story.story_status) && (
                <Btn variant="ghost" data-testid="crime-restore-btn" onClick={() => act(() => api.post(`/crime/${sel.story.id}/restore`), "Restored")}>Restore</Btn>
              )}
              {["published", "updated"].includes(sel.story.publication_status) && (
                <Btn variant="danger" data-testid="crime-unpublish-btn" onClick={() => setConfirmUnpub(true)}><EyeOff className="h-4 w-4" /> Unpublish</Btn>
              )}
            </div>
            {sel.story.is_test_record && (
              <p data-testid="crime-test-publish-notice" className="mt-3 text-xs text-amber-300/90 bg-amber-500/10 border border-amber-500/30 rounded-sm p-2">
                This is an automated test / QA record. It is private and cannot be published to the public website.
              </p>
            )}

            {isAdmin && (
              <div data-testid="crime-qa-safety-panel" className="mt-4 rounded-sm border border-amber-500/30 bg-amber-500/5 p-3">
                <div className="label-caps text-amber-400/80 mb-2 flex items-center gap-2"><ShieldAlert className="h-3.5 w-3.5" /> QA Safety · Record Classification</div>
                {!sel.story.is_test_record ? (
                  <>
                    <p className="text-xs text-zinc-500 mb-3">Marking this record as TEST / QA keeps it private and blocks it from ever being published.</p>
                    <Btn variant="ghost" data-testid="crime-mark-test-btn" onClick={() => setMarkModal(true)} className="w-full justify-center border-amber-500/40 text-amber-300">Mark as TEST / QA</Btn>
                  </>
                ) : (
                  <button data-testid="crime-unmark-test-btn" onClick={() => setUnmarkModal(true)} className="w-full text-center text-xs text-zinc-500 hover:text-zinc-300 underline underline-offset-2 py-1">Remove TEST / QA marker (advanced)</button>
                )}
              </div>
            )}

            <div className="mt-4 label-caps text-zinc-500">Add chronological update</div>
            <div className="mt-2 space-y-2">
              <input className={inputCls} placeholder="Update headline" data-testid="crime-update-headline" value={upd.headline} onChange={(e) => setUpd({ ...upd, headline: e.target.value })} />
              <textarea className={inputCls} rows={2} placeholder="New confirmed information" value={upd.new_confirmed} onChange={(e) => setUpd({ ...upd, new_confirmed: e.target.value })} />
              <select className={inputCls} value={upd.legal_status_change} onChange={(e) => setUpd({ ...upd, legal_status_change: e.target.value })}>
                <option value="">— no legal status change —</option>
                {LEGAL.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
              <Btn data-testid="crime-update-btn" onClick={() => act(() => api.post(`/crime/${sel.story.id}/update`, { ...upd, legal_status_change: upd.legal_status_change || null }), "Update added")}>Add Update</Btn>
            </div>

            <div className="mt-4 label-caps text-zinc-500">Material correction</div>
            <CorrectionForm onSubmit={(exp) => act(() => api.post(`/crime/${sel.story.id}/correction`, { explanation: exp }), "Correction recorded")} />

            <div className="mt-4 text-xs text-zinc-500">Updates: {sel.updates.length} · Legal status: {sel.story.legal_status}</div>
          </Card>
        )}
      </div>

      <Modal open={confirmUnpub} onClose={() => { if (!unpubBusy) setConfirmUnpub(false); }} title="Remove this story from the public website?">
        <p className="text-sm text-zinc-400" data-testid="crime-unpublish-explanation">
          The story will no longer appear on the public Crime Watch page or in public search. The private record, publication history, updates, corrections and audit trail will be preserved.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <Btn variant="ghost" data-testid="crime-unpublish-cancel" disabled={unpubBusy} onClick={() => setConfirmUnpub(false)}>Cancel</Btn>
          <Btn variant="danger" data-testid="crime-unpublish-confirm" disabled={unpubBusy} onClick={doUnpublish}>{unpubBusy ? "Unpublishing…" : "Unpublish"}</Btn>
        </div>
      </Modal>

      <Modal open={markModal} onClose={() => { if (!markBusy) setMarkModal(false); }} title="Mark as TEST / QA?">
        <p className="text-sm text-zinc-400" data-testid="crime-mark-explanation">This record will remain private and cannot be published while marked as TEST / QA.</p>
        <div className="mt-5 flex justify-end gap-2">
          <Btn variant="ghost" data-testid="crime-mark-cancel" disabled={markBusy} onClick={() => setMarkModal(false)}>Cancel</Btn>
          <Btn data-testid="crime-mark-confirm" disabled={markBusy} onClick={() => setTestMarker(true)}>{markBusy ? "Working…" : "Confirm"}</Btn>
        </div>
      </Modal>

      <Modal open={unmarkModal} onClose={() => { if (!markBusy) setUnmarkModal(false); }} title="Remove TEST / QA marker?">
        <p className="text-sm text-amber-300/90" data-testid="crime-unmark-explanation">Warning: removing the TEST / QA marker makes this record eligible for publication again. It will NOT be published automatically — you must publish it deliberately.</p>
        <div className="mt-5 flex justify-end gap-2">
          <Btn variant="ghost" data-testid="crime-unmark-cancel" disabled={markBusy} onClick={() => setUnmarkModal(false)}>Cancel</Btn>
          <Btn variant="danger" data-testid="crime-unmark-confirm" disabled={markBusy} onClick={() => setTestMarker(false)}>{markBusy ? "Working…" : "Remove marker"}</Btn>
        </div>
      </Modal>

      <Modal open={open} onClose={() => setOpen(false)} title="New Crime Story" wide>
        <form onSubmit={create} className="space-y-3">
          <Field label="Headline"><input data-testid="crime-headline-input" className={inputCls} required value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} /></Field>
          <Field label="Summary"><textarea className={inputCls} rows={2} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Country"><input className={inputCls} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></Field>
            <Field label="Geographic region"><select className={inputCls} value={form.geographic_region} onChange={(e) => setForm({ ...form, geographic_region: e.target.value })}>{REGIONS.map((r) => <option key={r}>{r}</option>)}</select></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Crime category"><select className={inputCls} value={form.crime_category} onChange={(e) => setForm({ ...form, crime_category: e.target.value })}>{CATS.map((c) => <option key={c}>{c}</option>)}</select></Field>
            <Field label="Legal status"><select className={inputCls} value={form.legal_status} onChange={(e) => setForm({ ...form, legal_status: e.target.value })}>{LEGAL.map((l) => <option key={l}>{l}</option>)}</select></Field>
          </div>
          <Field label="Confirmed facts (public)"><textarea className={inputCls} rows={2} value={form.confirmed_facts} onChange={(e) => setForm({ ...form, confirmed_facts: e.target.value })} /></Field>
          <Field label="Allegations (public, unproven)"><textarea className={inputCls} rows={2} value={form.allegations} onChange={(e) => setForm({ ...form, allegations: e.target.value })} /></Field>
          <Field label="Internal notes (never public)"><textarea className={inputCls} rows={2} value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} /></Field>
          <Btn data-testid="crime-save-btn" type="submit" className="w-full justify-center">Create Story</Btn>
        </form>
      </Modal>
    </div>
  );
}

function CorrectionForm({ onSubmit }) {
  const [exp, setExp] = useState("");
  return (
    <div className="mt-2 space-y-2">
      <textarea className={inputCls} rows={2} placeholder="Correction explanation" data-testid="crime-correction-input" value={exp} onChange={(e) => setExp(e.target.value)} />
      <Btn variant="ghost" data-testid="crime-correction-btn" onClick={() => { if (exp.trim()) { onSubmit(exp); setExp(""); } }}>Record Correction</Btn>
    </div>
  );
}
